import { IContextualMenuProps } from '@fluentui/react';
import { IPIconButton, IPText, IPTooltipHost } from 'baseComponents';
import { IPCallout } from 'baseComponents/Callout/IPCallout';
import { IPStack } from 'baseComponents/Stack/IPStack';
import EditTextFieldCallOut from 'components/CallOuts/EditTextFieldCallOut';
import RichTextEditor from 'components/Text/RichTextEditor';
import RichTextReadOnly from 'components/Text/RichTextReadOnly';
import {
  commentIcon,
  deleteIcon,
  editIcon,
  globalStackTokensGapExtraSmall,
  globalStackTokensGapSmall,
} from 'globalStyles';
import { TaskCheckListItem, TaskCheckListItemState } from 'models/tasks/taskHelperClasses';
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CallOutRowStyle, CircleRingIcon, SkypeCircleCheckIcon, StatusErrorFullIcon } from './CheckListStyles';

interface ICheckListItemRow {
  index: number;
  statusButtonId: string;
  showIcon: number | undefined;
  isCallOutOpen: TaskCheckListItem | undefined;
  isEditCallOutOpen: TaskCheckListItem | undefined;
  item: TaskCheckListItem;
  allowEdit: boolean;
  allowFill: boolean;
  // commenting is decoupled from filling: in a template or series master the comment is a pre-filled
  // instruction (edit), in a task instance it is the answer (fill). ChecklistContainer decides which.
  allowComment: boolean;
  onComment: (item: TaskCheckListItem, comment: string | undefined) => void;
  onDelete: (item: TaskCheckListItem) => void;
  onUpdateState: (item: TaskCheckListItem, state: TaskCheckListItemState) => void;
  onUpdateDescription: (item: TaskCheckListItem, description: string) => void;
  setIsEditCallOutOpen: (_itm: TaskCheckListItem | undefined) => void;
  setIsCallOutOpen: (_itm: TaskCheckListItem | undefined) => void;
  setShowIcon: (_itm: number | undefined) => void;
}

const getStatusIcon = (state: TaskCheckListItemState, id: string, allowFill: boolean): JSX.Element | null => {
  if (state === TaskCheckListItemState.Success)
    return <IPIconButton id={id} iconProps={SkypeCircleCheckIcon} disabled={!allowFill} />;
  if (state === TaskCheckListItemState.Failed)
    return <IPIconButton id={id} iconProps={StatusErrorFullIcon} disabled={!allowFill} />;
  if (state === TaskCheckListItemState.ToDo)
    return <IPIconButton id={id} iconProps={CircleRingIcon} disabled={!allowFill} />;

  return null;
};

export const CheckListItemRow = (props: ICheckListItemRow) => {
  const { t } = useTranslation(['task', 'translation']);
  const [editComment, setEditComment] = useState<boolean>(false);
  const [comment, setComment] = useState<string | undefined>(props.item.comment);

  //When comment is too long, disable editing. Counted on the serialized HTML, so formatting tags count.
  const maxCommentLength = 2048;
  const commentTooLong = (props.item.comment?.length ?? 0) > maxCommentLength;

  useEffect(() => {
    setComment(props.item.comment);
  }, [props.item]);

  const updateCheckListItemState = (state: TaskCheckListItemState) => {
    props.onUpdateState(props.item, state);
    props.setIsCallOutOpen(undefined);
  };

  const updateComment = (value: string | undefined) => {
    props.onComment(props.item, value);
  };

  const itemEditMenuProps: IContextualMenuProps = {
    items: [
      {
        key: 'edit',
        text: t('task:CheckList.Operations.Edit'),
        iconProps: editIcon,
        onClick: () => {
          if (!props.item) return;

          props.setIsEditCallOutOpen(props.item);
        },
      },
      {
        key: 'delete',
        text: t('task:CheckList.Operations.Remove'),
        iconProps: deleteIcon,
        onClick: () => {
          if (!props.item) return;
          props.onDelete(props.item);
        },
      },
    ],
    directionalHintFixed: true,
    beakWidth: 0,
  };

  const getOperationOptions = () => {
    if ((!props.allowFill && !props.allowEdit && !props.allowComment) || props.showIcon !== props.index)
      return <span />;

    return (
      <IPStack horizontal>
        {props.allowComment && (
          // without allowComment the comment editor is read-only, so the button would be a dead control
          <IPStack.Item>
            <IPTooltipHost content={t('task:CheckList.AddComment')}>
              <IPIconButton iconProps={commentIcon} onClick={() => setEditComment(true)} disabled={commentTooLong} />
            </IPTooltipHost>
          </IPStack.Item>
        )}
        {props.allowEdit && (
          <IPStack.Item>
            <IPIconButton menuProps={itemEditMenuProps} />
          </IPStack.Item>
        )}
      </IPStack>
    );
  };

  //
  // Main render
  //
  return (
    <Fragment>
      <IPStack
        horizontal
        styles={{ root: { height: '100%' } }}
        verticalAlign="start"
        onMouseOver={() => {
          props.setShowIcon(props.index);
        }}
        onMouseLeave={() => {
          props.setShowIcon(undefined);
        }}
        tokens={globalStackTokensGapSmall}
      >
        <IPStack.Item
          styles={{ root: { height: '100%' } }}
          onClick={() => {
            props.setIsCallOutOpen(props.item);
          }}
        >
          <IPStack.Item grow>{getStatusIcon(props.item.state, props.statusButtonId, props.allowFill)}</IPStack.Item>
        </IPStack.Item>
        <IPStack.Item styles={{ root: { paddingTop: 3, width: '100%' } }}>
          <IPStack tokens={globalStackTokensGapExtraSmall}>
            <IPStack.Item
              onClick={() => {
                props.setIsCallOutOpen(props.item);
              }}
            >
              <RichTextReadOnly html={props.item?.description} hideBorder={true} linkify={true} stripParagraph={true} />
            </IPStack.Item>
            <IPStack.Item>
              {(props.item?.comment || editComment) && (
                <RichTextEditor
                  html={comment}
                  cleanAiPaste
                  directEdit={props.allowComment && editComment}
                  readOnly={!props.allowComment || commentTooLong}
                  onChange={(value) => {
                    setComment(value);
                  }}
                  onAccept={(value, accept) => {
                    setEditComment(false);
                    if (accept) {
                      updateComment(value || undefined);
                    } else {
                      setComment(props.item?.comment);
                    }
                  }}
                  onStartEdit={() => {
                    setEditComment(true);
                  }}
                  placeholder={t('task:CheckList.AddComment')}
                  onBlur={(lastValue) => {
                    setEditComment(false);
                    updateComment(lastValue || undefined); //auto save on blur; normalize empty to undefined like onAccept
                  }}
                  maxLength={maxCommentLength}
                  maxHeight={300}
                  hideRemainingChars={true}
                  hideBorder={true}
                  hideEditIcon={true}
                  disallowFullscreen={true}
                />
              )}
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
        <IPStack.Item styles={{ root: { minWidth: '60px' } }}>
          <IPStack horizontal horizontalAlign="end">
            <IPStack.Item>{getOperationOptions()}</IPStack.Item>
          </IPStack>
        </IPStack.Item>
      </IPStack>
      {props.allowFill && props.isCallOutOpen && props.isCallOutOpen.id === props.item.id && (
        <IPCallout
          styles={{
            root: {
              padding: '10px 12px',
            },
          }}
          setInitialFocus
          target={`#${props.statusButtonId}`}
          onDismiss={() => {
            props.setIsCallOutOpen(undefined);
          }}
          beakWidth={16}
        >
          <IPStack>
            <IPStack
              verticalAlign="center"
              horizontal
              tokens={globalStackTokensGapSmall}
              onClick={() => {
                updateCheckListItemState(TaskCheckListItemState.Success);
              }}
              styles={CallOutRowStyle}
            >
              <IPIconButton iconProps={SkypeCircleCheckIcon} />
              <IPText>{t('task:CheckList.Success')}</IPText>
            </IPStack>
            <IPStack
              verticalAlign="center"
              horizontal
              tokens={globalStackTokensGapSmall}
              onClick={() => {
                updateCheckListItemState(TaskCheckListItemState.Failed);
              }}
              styles={CallOutRowStyle}
            >
              <IPIconButton iconProps={StatusErrorFullIcon} />
              <IPText>{t('task:CheckList.Failed')}</IPText>
            </IPStack>
            <IPStack
              horizontal
              verticalAlign="center"
              tokens={globalStackTokensGapSmall}
              onClick={() => {
                updateCheckListItemState(TaskCheckListItemState.ToDo);
              }}
              styles={CallOutRowStyle}
            >
              <IPIconButton iconProps={CircleRingIcon} />
              <IPText>{t('task:CheckList.Todo')}</IPText>
            </IPStack>
          </IPStack>
        </IPCallout>
      )}
      {props.isEditCallOutOpen && props.isEditCallOutOpen.id === props.item.id && (
        <EditTextFieldCallOut
          isVisible={true}
          targetId={props.statusButtonId}
          value={props.isEditCallOutOpen.description}
          onClose={() => {
            props.setIsEditCallOutOpen(undefined);
          }}
          onUpdate={(newText: string) => {
            if (props.isEditCallOutOpen) {
              props.onUpdateDescription(props.isEditCallOutOpen, newText);
            }
          }}
          maxLenght={4000}
          width={500}
          isHtml={true}
        />
      )}
    </Fragment>
  );
};
